import React from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { styled } from "@mui/material/styles";
import parse from "html-react-parser";
import ProTip from "../../../ProTip";

export const Styled = styled(Box, {
  shouldForwardProp: (prop) => prop !== "condition",
})(({ theme }) => ({
  margin: "1rem auto",
  alignItems: "flex-start",
  display: "flex",
  "& .MuiTypography-root": {
    fontSize: "0.90rem",
    // color: undefined,
    "& span:first-of-type": {
      fontWeight: 500,
      textShadow: "none",
      ...theme.applyStyles("light", { fontWeight: 600 }),
    },
    width: "100%",
    ...theme.applyStyles("light", { color: "#000" }),
  },
  "& .formLabel": {
    wordWrap: "break-word",
  },
  "& .defaultStyle": {
    color: "#71cbf5",
    fontWeight: "400 !important",
    ...theme.applyStyles("light", { color: undefined }),
  },
  "& .MuiLink-root": { fontSize: "0.90rem", color: "#31708f" },
  "& > .MuiSvgIcon-root": { marginRight: ".5rem" },
  "& .MuiIconButton-root": { padding: 0 },

  variants: [
    {
      props: ({ condition }) => condition.isDg,
      style: { margin: "0.3125rem auto" },
    },
    {
      props: ({ condition }) => condition.hasMaxWidth,
      style: {
        "& .MuiTypography-root": { width: "max-content" },
      },
    },
  ],
}));

const proTipProps = {
  title: "Info",
  showTitle: true,
  actionButton: [
    { value: "notUseful", label: "NOT USEFUL" },
    { value: "useful", label: "USEFUL" },
  ],
  showActionBtn: true,
};

function Label(props) {
  const {
    label,
    value,
    isError,
    showIcon,
    isMasked = false,
    showMaskedIcon = false,
    handleMaskedToggle,
    isDg = false,
    singlePageLayoutIcon,
    isSinglePageLayout = false,
    linkUrl,
    hasMaxWidth = false,
    proTipDescription,
  } = props;
  const customStyle = { isDg, hasMaxWidth };
  const applyPadding =
    showIcon || isDg || (!singlePageLayoutIcon && isSinglePageLayout);

  const labelName = parse(label);

  const proTip = proTipDescription ? (
    <ProTip
      fieldKey=""
      proTipProps={{
        ...proTipProps,
        content: <Typography component="span">{proTipDescription}</Typography>,
      }}
    />
  ) : null;

  return (
    <Styled condition={customStyle} tabIndex={0}>
      {showIcon && !isError && <CheckIcon color="success" aria-label="Valid" />}
      {showIcon && isError && <ClearIcon color="error" aria-label="Error" />}
      <Typography
        sx={[applyPadding ? { pl: 0 } : { pl: "2rem" }]}
        className="formLabel"
      >
        <span>
          {labelName}
          {proTip}:{" "}
        </span>
        {!linkUrl && (
          <span className="wrapper">
            <span className="defaultStyle">{value}</span>
          </span>
        )}
        {linkUrl && (
          <Typography
            component={Link}
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${label} ${value}`}
          >
            {value}
          </Typography>
        )}
      </Typography>
      {showMaskedIcon && (
        <IconButton
          aria-label="toggle encrypted mask"
          onClick={handleMaskedToggle}
          size="small"
          sx={{ ml: "0.5rem", mt: "-2px" }}
        >
          {isMasked ? <VisibilityOffIcon /> : <VisibilityIcon />}
        </IconButton>
      )}
    </Styled>
  );
}

Label.propTypes = {
  label: PropTypes.string.isRequired,
  singlePageLayoutIcon: PropTypes.bool.isRequired,
  isSinglePageLayout: PropTypes.bool,
  hasMaxWidth: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  showIcon: PropTypes.bool,
  isError: PropTypes.bool,
  isDg: PropTypes.bool,
  isMasked: PropTypes.bool,
  showMaskedIcon: PropTypes.bool,
  handleMaskedToggle: PropTypes.func,
  linkUrl: PropTypes.string,
};
Label.defaultProps = {
  value: undefined,
  showIcon: undefined,
  isError: undefined,
  isDg: false,
  isMasked: false,
  showMaskedIcon: false,
  handleMaskedToggle: undefined,
  linkUrl: undefined,
  isSinglePageLayout: false,
  hasMaxWidth: false,
};
export default Label;
